import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material';
import { AlertTriangle, Star, X } from 'lucide-react';
import DataTable from '../../components/data_table/DataTable';
import Skeleton from '../../components/ui/Skeleton';
import KitTooltip from '../../components/ui/Tooltip';
import { controlResetClassName, cx } from '../../components/ui/classNames';
import { STRESS_HEAT_CUTS, stressHeatColor } from '../../styles/performanceMsiStyles';
import EventCardAnchor from './EventCard';
import { cellOf, cellTotal, cellValue } from './exploreColumns';
import { cellNotes, formatCompactUsd, formatDay, formatPercent, formatRange, formatWindow, reasonsText } from './exploreFormat';

// Each kind's colour, the same on its chips and timeline bands: your events in the theme's violet.
export const PERIOD_TAGS = Object.freeze({
    standard: { label: 'Standard', className: 'border-ui-line bg-tbl-stripe text-ui-muted' },
    found: { label: 'Found', className: 'border-ui-warning-line bg-ui-warning-bg text-ui-warning' },
    yours: { label: 'Yours', className: 'border-ui-event-line bg-ui-event-bg text-ui-event' },
});

export function PeriodTag({ tag }) {
    const { label, className } = PERIOD_TAGS[tag];
    return (
        <span className={cx('inline-flex h-4 items-center rounded border border-solid px-1 text-[9.5px] font-bold uppercase leading-none tracking-normal', className)}>
            {label}
        </span>
    );
}

PeriodTag.propTypes = { tag: PropTypes.oneOf(Object.keys(PERIOD_TAGS)).isRequired };

const CELL_BUTTON_CLASS_NAME = cx(
    controlResetClassName,
    'flex h-[var(--tbl-row-h)] w-full cursor-pointer items-center justify-center gap-0.5 border-0 bg-transparent p-0',
    'text-[12.5px] font-semibold tabular-nums text-content-primary',
    'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ui-focus',
);
const HEADER_BUTTON_CLASS_NAME = cx(
    controlResetClassName,
    'absolute right-1 top-1 inline-flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded border-0 bg-transparent p-0',
    'text-ui-muted hover:bg-ui-hover hover:text-ui-ink focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-ui-focus',
);
// The info button sits left of the header's ×, beside the tag.
const HEADER_INFO_CLASS_NAME = 'absolute right-6 top-1';
const SELECTED_CELL_CLASS_NAME = 'shadow-[inset_0_0_0_2px_rgb(var(--c-text))]';
// A market cell only shows its numbers on hover or focus; it opens no detail.
const MARKET_CELL_CLASS_NAME = cx(
    'flex h-[var(--tbl-row-h)] w-full items-center justify-center gap-0.5 text-[12.5px] font-semibold tabular-nums',
    'text-content-primary focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2',
    'focus-visible:outline-ui-focus',
);
const ACTUAL_TAG_CLASS_NAME = 'shrink-0 rounded border border-solid border-ui-line bg-tbl-stripe px-1 text-[9.5px] font-bold uppercase '
    + 'leading-4 text-ui-muted';

function PortfolioCell({ entry }) {
    const { row } = entry;
    const imputed = row?.imputed_cognive_ids?.length || 0;
    const facts = [
        Number.isInteger(row?.holding_count) ? `${row.holding_count} holding${row.holding_count === 1 ? '' : 's'}` : null,
        Number.isFinite(row?.gross_notional_usd) ? `${formatCompactUsd(row.gross_notional_usd)} gross` : null,
    ].filter(Boolean).join(' · ');
    return (
        <span className="flex min-w-0 items-center gap-1.5">
            {entry.baseline && <Star aria-label="Baseline" role="img" size={12} strokeWidth={2.25} fill="currentColor" className="shrink-0 text-ui-warning" />}
            <span className="grid min-w-0">
                <span className="truncate font-semibold" title={entry.label}>{entry.label}</span>
                {entry.pending ? (
                    <>
                        <Skeleton width={96} className="mt-[3px]" />
                        <span className="sr-only">Loading its replay</span>
                    </>
                ) : facts && <span className="truncate text-[10.5px] font-medium text-content-secondary">{facts}</span>}
            </span>
            {imputed > 0 && (
                <KitTooltip content={`${imputed} holding${imputed === 1 ? ' is' : 's are'} replayed with imputed factor exposures.`}>
                    <span tabIndex={0} className="shrink-0 rounded border border-solid border-ui-warning-line bg-ui-warning-bg px-1 text-[9.5px] font-bold uppercase text-ui-warning">
                        Imputed {imputed}
                    </span>
                </KitTooltip>
            )}
            {row && !row.available && (
                <KitTooltip content={reasonsText(row.reasons) || 'This portfolio is unavailable.'}>
                    <span tabIndex={0} role="img" aria-label={`${entry.label} is unavailable: ${reasonsText(row.reasons) || 'no reason given'}`} className="inline-flex shrink-0 text-ui-warning">
                        <AlertTriangle aria-hidden="true" size={14} strokeWidth={2.25} />
                    </span>
                </KitTooltip>
            )}
        </span>
    );
}

// A market's name, tagged as actual history rather than a replay.
function MarketCell({ entry }) {
    const { row } = entry;
    return (
        <span className="flex min-w-0 items-center gap-1.5">
            <span className="grid min-w-0">
                <span className="truncate font-semibold" title={entry.label}>{entry.label}</span>
                {entry.pending ? (
                    <>
                        <Skeleton width={96} className="mt-[3px]" />
                        <span className="sr-only">Loading its returns</span>
                    </>
                ) : <span className="truncate text-[10.5px] font-medium text-content-secondary">ETF total return</span>}
            </span>
            <KitTooltip content="The ETF's actual returns over each event's dates, dividends reinvested. Not a replay of your holdings.">
                <span tabIndex={0} className={ACTUAL_TAG_CLASS_NAME}>Actual</span>
            </KitTooltip>
            {row && !row.available && (
                <KitTooltip content={reasonsText(row.reasons) || 'This market is unavailable.'}>
                    <span tabIndex={0} role="img" aria-label={`${entry.label} is unavailable: ${reasonsText(row.reasons) || 'no reason given'}`} className="inline-flex shrink-0 text-ui-warning">
                        <AlertTriangle aria-hidden="true" size={14} strokeWidth={2.25} />
                    </span>
                </KitTooltip>
            )}
        </span>
    );
}

// The event's tag, title and dates; its card peeks on hover and the info
// button pins it.
function PeriodHeader({ column, baselineLabel, onEventAction }) {
    return (
        <EventCardAnchor column={column} baselineLabel={baselineLabel} onAction={onEventAction}
            className="grid min-w-0 gap-0.5 py-1 text-left" infoClassName={HEADER_INFO_CLASS_NAME}>
            <span className="flex"><PeriodTag tag={column.tag} /></span>
            <span className="line-clamp-3 whitespace-normal break-words text-[11.5px] font-semibold leading-[1.2] text-content-primary">{column.name}</span>
            <span className="truncate text-[10.5px] font-medium text-content-secondary">{formatWindow(column.start_date, column.end_date)}</span>
        </EventCardAnchor>
    );
}

function cellTooltip(entry, column, baselineEntry, cell) {
    const title = `${entry.label} · ${column.name}`;
    if (!cell?.available) return <><span className="block font-bold">{title}</span>{reasonsText(cell?.reasons) || 'No result for this period.'}</>;
    const lines = [
        ['Factor result', formatPercent(cell.total, 2)],
        ['Stock-specific range', formatRange(cell.specific_range, 2)],
    ];
    if (!entry.baseline && baselineEntry) {
        const gap = cellValue(entry.row, column, 'gap', baselineEntry.row);
        lines.push([`vs ${baselineEntry.label}`, formatPercent(gap, 2)]);
    }
    lines.push(['Trading days', String(cell.trading_days ?? '—')]);
    const warnings = cellNotes(cell);
    return (
        <>
            <span className="block font-bold">{title}</span>
            {lines.map(([label, value]) => <span key={label} className="block"><span className="opacity-70">{label} </span>{value}</span>)}
            {warnings && <span className="mt-0.5 block">{warnings}</span>}
        </>
    );
}

function marketTooltip(entry, column, cell) {
    const title = `${entry.label} · ${column.name}`;
    if (!cell?.available) return <><span className="block font-bold">{title}</span>{reasonsText(cell?.reasons) || 'No actual return for this period.'}</>;
    const lines = [
        ['Actual total return', formatPercent(cell.total, 2)],
        ['From the close of', formatDay(cell.base_date)],
        ['To the close of', formatDay(cell.effective_end_date)],
        ['Trading days', String(cell.trading_days ?? '—')],
    ];
    const notes = reasonsText(cell.reasons);
    return (
        <>
            <span className="block font-bold">{title}</span>
            {lines.map(([label, value]) => <span key={label} className="block"><span className="opacity-70">{label} </span>{value}</span>)}
            {notes && <span className="mt-0.5 block">{notes}</span>}
            <span className="mt-0.5 block opacity-70">The ETF&apos;s price history with dividends reinvested, not a replay.</span>
        </>
    );
}

// A market's own actual return in either view: never a gap to the baseline.
function MarketGridCell({ entry, column }) {
    const { row } = entry;
    if (column.pending || entry.pending) return <span aria-hidden="true" className="flex justify-center"><Skeleton width="55%" /></span>;
    if (!row?.available) return <span aria-hidden="true" className="text-content-secondary">—</span>;
    const cell = cellOf(row, column);
    const total = cellTotal(row, column);
    const spoken = total === null ? reasonsText(cell?.reasons) || 'no result' : `actual ${formatPercent(total)}`;
    return (
        <KitTooltip content={marketTooltip(entry, column, cell)}>
            <span tabIndex={0} role="img" className={cx(MARKET_CELL_CLASS_NAME, total === null && '!font-medium !text-content-secondary')}
                aria-label={`${entry.label}, ${column.name} ${formatWindow(column.start_date, column.end_date)}: ${spoken}`}>
                {formatPercent(total)}
                {total !== null && reasonsText(cell.reasons) && <span aria-hidden="true" className="text-[10px] text-ui-warning">●</span>}
            </span>
        </KitTooltip>
    );
}

function GridCell({ entry, column, basis, baselineEntry, selected, onSelect }) {
    const { row } = entry;
    // A column or portfolio added since this frame loaded waits for the next one.
    if (column.pending || entry.pending) return <span aria-hidden="true" className="flex justify-center"><Skeleton width="55%" /></span>;
    if (!row?.available) {
        return <span aria-hidden="true" className="text-content-secondary">—</span>;
    }
    const cell = cellOf(row, column);
    const asBaseline = basis === 'gap' && entry.baseline;
    const value = cellValue(row, column, basis, baselineEntry?.row);
    const text = asBaseline ? '—' : formatPercent(value);
    const warned = Boolean(cellNotes(cell));
    const spoken = asBaseline ? 'baseline' : cell?.available ? text : reasonsText(cell?.reasons) || 'no result';
    return (
        <KitTooltip content={cellTooltip(entry, column, baselineEntry, cell)}>
            <button
                type="button"
                className={cx(CELL_BUTTON_CLASS_NAME, (asBaseline || !cell?.available) && '!font-medium !text-content-secondary')}
                aria-pressed={selected}
                aria-label={`${entry.label}, ${column.name} ${formatWindow(column.start_date, column.end_date)}: ${spoken}`}
                onClick={() => onSelect(entry.key, column.id)}
            >
                {text}
                {warned && <span aria-hidden="true" className="text-[10px] text-ui-warning">●</span>}
            </button>
        </KitTooltip>
    );
}

/**
 * Portfolios as rows and events as columns, each cell the event's factor
 * replay (or its gap to the baseline) on the diverging heat scale. The first
 * column is pinned; a header's card describes its event, and its × puts the
 * event back on the bench; a cell press selects it for the episode detail.
 * `markets` follow as rows tagged Actual: each cell is the ETF's own actual
 * return in either view, shown on hover, and opens nothing; it takes the
 * result colours, and none in the gap view, whose scale is for gaps.
 */
export default function StressGrid({
    entries, markets = [], columns, basis, baselineLabel, loading = false, selected = null, onSelectCell, onRemoveColumn,
    onEventAction = null,
}) {
    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';
    const baselineEntry = entries[0] || null;
    const tableColumns = useMemo(() => [
        {
            id: 'portfolio',
            header: 'Portfolio',
            size: 216,
            enableSorting: false,
            cell: ({ row: { original } }) => (original.market ? <MarketCell entry={original} /> : <PortfolioCell entry={original} />),
            meta: { wrap: true },
        },
        ...columns.map((column) => ({
            id: column.id,
            header: () => <PeriodHeader column={column} baselineLabel={baselineLabel} onEventAction={onEventAction} />,
            enableSorting: false,
            minSize: 132,
            cell: ({ row: { original } }) => (original.market ? <MarketGridCell entry={original} column={column} /> : (
                <GridCell entry={original} column={column} basis={basis} baselineEntry={baselineEntry}
                    selected={selected?.sourceKey === original.key && selected?.columnId === column.id} onSelect={onSelectCell} />
            )),
            meta: {
                grow: true,
                align: 'center',
                wrap: true,
                label: column.name,
                headerClassName: '!whitespace-normal !normal-case !align-top',
                headerControls: () => (
                    <KitTooltip content="Remove from the table">
                        <button type="button" className={HEADER_BUTTON_CLASS_NAME} onClick={() => onRemoveColumn(column.id)}
                            aria-label={`Remove ${column.name}, ${formatWindow(column.start_date, column.end_date)} from the table`}>
                            <X aria-hidden="true" size={13} strokeWidth={2.25} />
                        </button>
                    </KitTooltip>
                ),
                cellClassName: ({ row: { original } }) => cx(
                    '!p-0',
                    selected?.sourceKey === original.key && selected?.columnId === column.id && SELECTED_CELL_CLASS_NAME,
                ),
                cellStyle: ({ row: { original } }) => {
                    if (basis === 'gap' && original.baseline) return undefined;
                    if (original.market && basis === 'gap') return undefined;
                    const color = original.market
                        ? stressHeatColor(cellTotal(original.row, column), { basis, dark })
                        : stressHeatColor(cellValue(original.row, column, basis, baselineEntry?.row), { basis, dark });
                    return color ? { backgroundColor: color } : undefined;
                },
            },
        })),
    ], [columns, basis, dark, baselineEntry, baselineLabel, selected, onSelectCell, onRemoveColumn, onEventAction]);
    const rows = [...entries, ...markets];
    return (
        <DataTable
            aria-label="Stress grid"
            data={rows}
            columns={tableColumns}
            getRowId={(entry) => entry.key}
            pinnedColumns={['portfolio']}
            striped={false}
            loading={loading}
            loadingRowCount={Math.max(rows.length, 1)}
            rowHeight={46}
            headerHeight={92}
        />
    );
}

StressGrid.propTypes = {
    entries: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        baseline: PropTypes.bool,
        row: PropTypes.object,
        pending: PropTypes.bool,
    })).isRequired,
    markets: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        market: PropTypes.string.isRequired,
        row: PropTypes.object,
        pending: PropTypes.bool,
    })),
    columns: PropTypes.array.isRequired,
    basis: PropTypes.oneOf(['result', 'gap']).isRequired,
    baselineLabel: PropTypes.string.isRequired,
    loading: PropTypes.bool,
    selected: PropTypes.shape({ sourceKey: PropTypes.string, columnId: PropTypes.string }),
    onSelectCell: PropTypes.func.isRequired,
    onRemoveColumn: PropTypes.func.isRequired,
    onEventAction: PropTypes.func,
};

// The ramp from the strongest loss to the strongest gain, with the cuts at
// its ends, so colour is never the only signal of a number. With `markets`,
// it says how the Actual rows read.
export function StressLegend({ basis, markets = false }) {
    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';
    const cuts = STRESS_HEAT_CUTS[basis];
    const steps = [...cuts.slice().reverse().map((cut) => -cut), 0, ...cuts];
    const edge = formatPercent(cuts[cuts.length - 1], 0).replace(/^\+/, '');
    const grey = (cuts[0] * 100).toLocaleString('en-US', { maximumFractionDigits: 1 });
    return (
        <div aria-label="Colour scale" className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-content-secondary">
            <span>{basis === 'gap' ? 'Gap to baseline, % of opening gross:' : 'Factor result, % of opening gross:'}</span>
            <span>worse than −{edge}</span>
            <span aria-hidden="true" className="inline-flex items-center gap-0.5">
                {steps.map((value) => (
                    <i key={value} className="block h-2.5 w-4 rounded-sm border border-solid border-ui-line" style={{ backgroundColor: stressHeatColor(value, { basis, dark }) }} />
                ))}
            </span>
            <span>better than +{edge}</span>
            <span>· grey: within ±{grey}%</span>
            {markets && (
                <span>
                    {basis === 'gap' ? '· market rows keep their own actual return, uncoloured' : '· market rows: the ETF’s actual return, same colours'}
                </span>
            )}
        </div>
    );
}

StressLegend.propTypes = { basis: PropTypes.oneOf(['result', 'gap']).isRequired, markets: PropTypes.bool };
